import React, { useState, useEffect, useCallback, useRef } from 'react';
import useResizableSplitPane, { getLeaseAbstractionSplitStorageKey, getSplitPaneWidthCssValue } from '../useResizableSplitPane';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Checkbox } from '@cbrebuild/blocks';
import LeasePdfViewerPanel from '../LeaseInformation/LeasePdfViewerPanel';
import FileUploadLeaseComp from '../../file-upload/lease-comp/lease-comp';
import LeaseCompExtraFields from './LeaseCompExtraFields';
import LeaseAbstractionContext from '../../file-upload/contexts/leaseAbstractionContext';
import { createLinks } from '../BottomNav';
import { subscribeToDealExtractionStatus } from '../../../services/attachments-service';
import { dispatchLeaseExtractionRefreshRequest } from '../../../hooks/useLeaseExtractionPersistedDataRefresh';
import useFileAbstractionFields from '../../../hooks/useFileAbstractionFields';
import { updateCompData } from '../../../redux/actions/voucher/comp-data-actions';
import { fetchBillToSection } from '../../../redux/actions/financial-actions';
import { TRACKING_IDS, PARENT_KEYS } from '../../../constants/lease-abstarction';
import useVoucherValidation from '../useVoucherValidation';
import { validatePayload, getRandomId } from '../../../utils/validation-utils';
import {
  LEASE_ABSTRACTION_VALIDATION_PAGES,
  isLeaseAbstractionPageValidated,
  setLeaseAbstractionPageValidated,
} from '../../../utils/lease-abstraction-validation-storage';
import { compValidationSchema } from '../../file-upload/validation-schema';
import '../../file-upload/lease-abstraction.scss';

const parseBooleanValue = value => value === true || String(value).toLowerCase() === 'true';

const parseNumericValue = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeCustomRentEscalationSchedule = schedule => schedule.map((item) => {
  const rent = item.rent || item;
  return {
    months: parseInt(item.months, 10) || 0,
    is_percent: parseBooleanValue(rent.is_percent),
    rent_dollar: parseNumericValue(rent.rent_dollar),
    rent_percent: parseNumericValue(rent.rent_percent),
  };
});

const LeaseCompAbstractionTab = ({
  deal,
  dealFiles,
  abstractionLeaseFile,
  fetchFiles,
  uploadFile,
}) => {
  const [extractionResults, setExtractionResults] = useState([]);
  const [overrideFile, setOverrideFile] = useState(null);
  const [showMoreInstructions, setShowMoreInstructions] = useState(false);
  const [tAndCLeaseComp, setTAndCLeaseComp] = useState(() => isLeaseAbstractionPageValidated(
    deal.id,
    LEASE_ABSTRACTION_VALIDATION_PAGES.leaseComp,
  ));
  const [pdfPageData, setPdfPageData] = useState({ pageNumber: 1, annotationField: 'N/A' });
  const [currentHighlightedField, setCurrentHighlightedField] = useState('N/A');
  const [fieldErrorMap, setFieldErrorMap] = useState({});
  const requestedPersistedRefreshRef = useRef(null);
  const suppressPersistedRefreshForFileRef = useRef(null);
  const {
    containerRef,
    leftPaneWidth,
    minLeftWidth,
    maxLeftWidth,
    startResize,
    handleDividerKeyDown,
    dividerHintText,
  } = useResizableSplitPane({ storageKey: getLeaseAbstractionSplitStorageKey(deal.id) });

  const dispatch = useDispatch();
  const loadedSectionsRef = useRef({});
  const [modalFieldState, dispatchModalFieldState] = useFileAbstractionFields();
  const [responseState, dispatchResponseState] = useFileAbstractionFields();

  // Fetch files on mount if not already loaded (e.g. navigated directly to this tab or page refresh)
  useEffect(() => {
    if (!dealFiles || dealFiles.length === 0) {
      fetchFiles({ deal: deal.id, ordering: '-modified', page: 1, page_size: 25 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  useEffect(() => {
    loadedSectionsRef.current = {};
  }, [deal.id]);

  const loadSectionOnce = (sectionKey, fetchSection) => {
    if (loadedSectionsRef.current[sectionKey]) return;
    loadedSectionsRef.current[sectionKey] = true;
    fetchSection();
  };

  const loadGeneralInformation = () => loadSectionOnce('generalInformation', () => {
    dispatch(fetchBillToSection(deal.id));
  });

  const routerLocation = useLocation();
  const { dealType, isEarlyInvoice, compData, voucherId } = useSelector(state => ({
    dealType: state.deal.deal?.deal_type,
    isEarlyInvoice: state.voucher?.voucher?.is_early_invoice,
    compData: state.compData,
    voucherId: state.voucher?.voucher?.id,
  }));

  const { showPageValidation } = useVoucherValidation(voucherId, 4.1);

  const { prev: abstractionPrev, next: abstractionNext } = createLinks(dealType, routerLocation.pathname, isEarlyInvoice);

  useEffect(() => {
    setTAndCLeaseComp(isLeaseAbstractionPageValidated(
      deal.id,
      LEASE_ABSTRACTION_VALIDATION_PAGES.leaseComp,
    ));
  }, [deal.id]);

  const handleLeaseCompValidationChange = (checked) => {
    setTAndCLeaseComp(checked);
    setLeaseAbstractionPageValidated(deal.id, LEASE_ABSTRACTION_VALIDATION_PAGES.leaseComp, checked);
  };

  // ─── Resolve the active lease file ───
  const resolvedLeaseFile = (() => {
    // Only trust a file reference if it still exists in the current dealFiles list.
    // IMPORTANT: if dealFiles hasn't loaded yet (empty array on refresh) we must NOT
    // treat a stored entry as stale — that would delete it before files arrive.
    const filesLoaded = dealFiles && dealFiles.length > 0;
    const isValid = f => f && f.id && filesLoaded && dealFiles.some(df => df.id === f.id);
    if (isValid(abstractionLeaseFile)) return abstractionLeaseFile;
    try {
      const stored = localStorage.getItem(`abstractionVoucherFile_${deal.id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.id) {
          // If files are loaded and the entry is confirmed stale, clear it.
          // Otherwise (files not loaded yet, or file is found) keep / return it.
          if (filesLoaded && !dealFiles.some(df => df.id === parsed.id)) {
            localStorage.removeItem(`abstractionVoucherFile_${deal.id}`);
          } else {
            return parsed;
          }
        }
      }
    } catch (e) { /* ignore */ }
    if (filesLoaded) {
      const leaseFile = dealFiles.find(f => f.is_lease_document === true);
      if (leaseFile) return leaseFile;
      return dealFiles[0];
    }
    return null;
  })();

  // Clear overrideFile if the file it points to no longer exists
  useEffect(() => {
    if (overrideFile && (!dealFiles || !dealFiles.some(f => f.id === overrideFile.id))) {
      setOverrideFile(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealFiles]);

  const activeFile = overrideFile || resolvedLeaseFile;

  // ─── Subscribe to shared deal-level extraction updates for the active file ───
  useEffect(() => {
    if (!(activeFile && activeFile.id)) {
      setExtractionResults([]);
      return undefined;
    }

    return subscribeToDealExtractionStatus({
      dealId: deal.id,
      fileId: activeFile.id,
      onUpdate: (result) => setExtractionResults(result ? [result] : []),
      onError: () => setExtractionResults([]),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile && activeFile.id, deal.id]);

  const extractionResult = extractionResults[0];
  const extractionStatus = extractionResult?.status;

  useEffect(() => {
    if (extractionStatus !== 'Success' || !activeFile?.id) return;
    const refreshKey = `${activeFile.id}:${extractionResult?.id || extractionResult?.status_text || extractionStatus}`;
    if (requestedPersistedRefreshRef.current === refreshKey) return;
    if (suppressPersistedRefreshForFileRef.current === activeFile.id) {
      requestedPersistedRefreshRef.current = refreshKey;
      return;
    }
    requestedPersistedRefreshRef.current = refreshKey;
    dispatchLeaseExtractionRefreshRequest({
      dealId: deal.id,
      fileId: activeFile.id,
      refreshKey,
      extractionResult,
    });
  }, [activeFile, deal.id, extractionResult, extractionStatus]);

  // ─── Context field state handlers ───
  const modalFieldChangeHandler = useCallback(
    (field, value, type = 'single') => { dispatchModalFieldState({ field, value, type }); },
    [dispatchModalFieldState],
  );
  const setResponseState = useCallback(
    (field, value, type = 'single') => { dispatchResponseState({ field, value, type }); },
    [dispatchResponseState],
  );

  // ─── Save a comp field value to the DB on blur/select-change ───
  const saveCompField = useCallback((fieldName, value) => {
    if (!compData?.id) return;
    if (fieldName === 'leaseEscalation') {
      dispatch(updateCompData(compData.id, { rent_escalation_is_custom: value !== 'Fixed' }));
      return;
    }
    if (fieldName === 'customLeaseEscalation' && Array.isArray(value)) {
      dispatch(updateCompData(compData.id, { rent_escalation_custom: normalizeCustomRentEscalationSchedule(value) }));
      return;
    }
    if (fieldName === 'fixedLeaseEscalation' && value && typeof value === 'object') {
      // Only send keys the caller actually included -- the untouched $/% field
      // must stay omitted, not defaulted, or it overwrites the saved value with 0.
      const payload = {};
      if (value.rent_escalation_is_percent !== undefined) payload.rent_escalation_is_percent = value.rent_escalation_is_percent;
      if (value.rent_escalation_dollar !== undefined) payload.rent_escalation_dollar = value.rent_escalation_dollar;
      if (value.rent_escalation_percent !== undefined) payload.rent_escalation_percent = value.rent_escalation_percent;
      dispatch(updateCompData(compData.id, payload));
      return;
    }
    const dbField = TRACKING_IDS[fieldName];
    if (dbField) dispatch(updateCompData(compData.id, { [dbField]: value }));
  }, [dispatch, compData]);

  // ─── Accept chip: save to compData ───
  const individualModalFieldChangeHandler = (params) => {
    const { stateVariable, child_value } = params;

    // customLeaseEscalation: handle before the stateField guard because
    // responseState.customLeaseEscalation is only set when extraction results
    // exist — manual edits on un-extracted deals would otherwise be silently
    // dropped. Also bypass the string-equality check: arrays of objects always
    // stringify to "[object Object],[object Object]" regardless of their values,
    // so the check always blocks saves for edits that don't change the row count.
    if (stateVariable === 'customLeaseEscalation') {
      if (!compData?.id || !Array.isArray(child_value)) return;
      dispatch(updateCompData(compData.id, { rent_escalation_custom: normalizeCustomRentEscalationSchedule(child_value) }));
      return;
    }

    const stateField = responseState[stateVariable];
    if (!stateField) return;
    if (!compData?.id) return;

    // leaseEscalation: save only the Fixed/Custom flag. Schedule row edits are
    // handled by the customLeaseEscalation branch above.
    if (stateVariable === 'leaseEscalation') {
      const isCustom = child_value === 'Custom' || child_value === true || String(child_value).toLowerCase() === 'true';
      dispatch(updateCompData(compData.id, { rent_escalation_is_custom: isCustom }));
      return;
    }

    const normalizedChildValue = String(child_value).trim();
    const normalizedResponseValue = String(stateField.value).trim();
    if (normalizedChildValue === normalizedResponseValue) return;
    const dbField = TRACKING_IDS[stateVariable];
    if (!dbField) return;
    dispatch(updateCompData(compData.id, { [dbField]: child_value }));
  };

  const validateLeaseAbstractionFields = () => {
    if (isEarlyInvoice) {
      setFieldErrorMap({});
      return {
        isPayloadValid: true,
        errorMessages: '',
      };
    }
    const { isPayloadValid, errorMap } = validatePayload(compValidationSchema, modalFieldState);
    setFieldErrorMap(errorMap);
    return {
      isPayloadValid,
      errorMessages: Object.entries(errorMap).map(([f, m]) => `${f}: ${m}`).join(', '),
    };
  };

  const clearFieldError = useCallback((fieldName) => {
    setFieldErrorMap(prev => ({ ...prev, [fieldName]: undefined }));
  }, []);

  // ─── Initialize extraction chips from AI results ───
  useEffect(() => {
    if (!extractionResults || extractionResults.length === 0) return;
    const results = extractionResults;
    if (!results[0]?.data_extract) return;
    const annotationsData = results[0]?.data_extract?.annotations || {};
    const getFieldConfidence = (basePath, valuePath) => {
      const key = `${basePath}_${valuePath.replace(/\./g, '_')}`;
      const blocks = annotationsData[key];
      if (!blocks || Object.keys(blocks).length === 0) return null;
      const scores = Object.values(blocks)
        .map(b => b.text_confidence)
        .filter(s => typeof s === 'number');
      return scores.length > 0 ? Math.min(...scores) : null;
    };
    const initField = (fieldName, valuePath, basePath) => {
      const fieldInfo = valuePath.split('.').reduce(
        (acc, part) => acc && acc[part],
        results[0]?.data_extract?.[basePath],
      ) || '';
      const rawValue = fieldInfo?.value || fieldInfo?.extracted_value || '';
      const fieldInfoValue = {
        extractedValue: rawValue,
        toolTip: {
          section: fieldInfo?.section,
          paragraph: fieldInfo?.paragraph,
          pageIndex: fieldInfo?.page_index ? `${fieldInfo.page_index}*` : fieldInfo?.page_index,
          confidence: getFieldConfidence(basePath, valuePath),
        },
      };
      modalFieldChangeHandler(fieldName, fieldInfoValue, 'single');
      setResponseState(fieldName, { value: rawValue, toolTip: fieldInfoValue.toolTip }, 'single');
    };
    initField('tenantRepresentCompany', 'tenant_represented_by', 'comp_info');
    initField('tenantRepresentAgent', 'tenant_represented_by_agent', 'comp_info');
    initField('landlordRepresentCompany', 'landlord_represented_by', 'comp_info');
    initField('landlordRepresentAgent', 'landlord_represented_by_agent', 'comp_info');
    initField('baseRent', 'base_rent', 'comp_info');
    initField('freeRent', 'free_rent', 'comp_info');
    // leaseEscalation: map raw boolean extracted value to human-readable label
    // so the chip shows "Fixed"/"Custom" instead of "false"/"true"
    const escalationField = results[0]?.data_extract?.comp_info?.rent_escalation_is_custom;
    if (escalationField) {
      const rawEscalationVal = escalationField?.value || escalationField?.extracted_value || '';
      const mappedLabel = String(rawEscalationVal).toLowerCase() === 'true' ? 'Custom'
        : String(rawEscalationVal).toLowerCase() === 'false' ? 'Fixed'
        : rawEscalationVal;
      const escalationInfoValue = {
        extractedValue: mappedLabel,
        toolTip: {
          section: escalationField?.section,
          paragraph: escalationField?.paragraph,
          pageIndex: escalationField?.page_index ? `${escalationField.page_index}*` : escalationField?.page_index,
          confidence: getFieldConfidence('comp_info', 'rent_escalation_is_custom'),
        },
      };
      modalFieldChangeHandler('leaseEscalation', escalationInfoValue, 'single');
      setResponseState('leaseEscalation', { value: mappedLabel, toolTip: escalationInfoValue.toolTip }, 'single');
    }
    // customLeaseEscalation is an array — handle separately from scalar initField
    const customEscalationField = results[0]?.data_extract?.comp_info?.rent_escalation_custom;
    if (customEscalationField) {
      const rawArr = customEscalationField?.value || customEscalationField?.extracted_value || [];
      const parseRentVal = v => (
        v != null && typeof v === 'object' && 'parsedValue' in v ? v.parsedValue : (v || '')
      );
      const mappedEscalations = Array.isArray(rawArr) && rawArr.length > 0
        ? rawArr.map(item => ({
          id: getRandomId(),
          months: item.months || '',
          rent: {
            is_percent: String(item.is_percent).toLowerCase() === 'true',
            rent_dollar: parseRentVal(item.rent_dollar),
            rent_percent: parseRentVal(item.rent_percent),
          },
        }))
        : [{ id: getRandomId(), rent: { is_percent: false, rent_dollar: '', rent_percent: '' }, months: '' }];
      const escalationInfoValue = {
        extractedValue: mappedEscalations,
        toolTip: {
          section: customEscalationField?.section,
          paragraph: customEscalationField?.paragraph,
          pageIndex: customEscalationField?.page_index ? `${customEscalationField.page_index}*` : customEscalationField?.page_index,
          confidence: getFieldConfidence('comp_info', 'rent_escalation_custom'),
        },
      };
      modalFieldChangeHandler('customLeaseEscalation', escalationInfoValue, 'single');
      setResponseState('customLeaseEscalation', { value: mappedEscalations, toolTip: escalationInfoValue.toolTip }, 'single');
    }
    initField('fixedLeaseEscalation', 'rent_escalation_dollar', 'comp_info');
    initField('tenantImprovementAllowance', 'tenant_improvement_allowance_amount_per_sqft', 'comp_info');
    initField('tenantImprovementDetails', 'tenant_improvements_detail', 'comp_info');
    initField('parkingRatio', 'parking_ratio', 'comp_info');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionResults]);

  // ─── Initialize editable values from compData (DB) ───
  useEffect(() => {
    if (!compData?.id) return;
    const isTenantRepDeal = deal.rep_role === 'tenant';
    const isLandlordRepDeal = deal.rep_role === 'landlord';
    const tenantRepresentedBy = compData.tenant_represented_by || (isTenantRepDeal ? 'CBRE' : '');
    const landlordRepresentedBy = compData.landlord_represented_by || (isLandlordRepDeal ? 'CBRE' : '');
    const compDataDefaults = {};

    if (isTenantRepDeal && !compData.tenant_represented_by) {
      compDataDefaults.tenant_represented_by = 'CBRE';
    }
    if (isLandlordRepDeal && !compData.landlord_represented_by) {
      compDataDefaults.landlord_represented_by = 'CBRE';
    }
    if (Object.keys(compDataDefaults).length > 0) {
      dispatch(updateCompData(compData.id, compDataDefaults));
    }

    modalFieldChangeHandler('tenantRepresentCompany', { value: tenantRepresentedBy }, 'single');
    modalFieldChangeHandler('tenantRepresentAgent', { value: compData.tenant_represented_by_agent || '' }, 'single');
    modalFieldChangeHandler('landlordRepresentCompany', { value: landlordRepresentedBy }, 'single');
    modalFieldChangeHandler('landlordRepresentAgent', { value: compData.landlord_represented_by_agent || '' }, 'single');
    modalFieldChangeHandler('baseRent', { value: compData.base_rent != null ? String(compData.base_rent) : '' }, 'single');
    modalFieldChangeHandler('freeRent', { value: compData.free_rent != null ? compData.free_rent : '' }, 'single');
    const escalationType = compData.rent_escalation_is_custom === false ? 'Fixed'
      : compData.rent_escalation_is_custom === true ? 'Custom' : '';
    modalFieldChangeHandler('leaseEscalation', { value: escalationType }, 'single');
    // Populate the custom escalation schedule rows from DB
    if (Array.isArray(compData.rent_escalation_custom) && compData.rent_escalation_custom.length > 0) {
      const dbSchedule = compData.rent_escalation_custom.map(item => ({
        id: getRandomId(),
        months: item.months != null ? item.months : '',
        rent: {
          // is_percent may come back as a string "False"/"True" from the API —
          // Boolean("False") === true, so we must parse explicitly.
          is_percent: item.is_percent === true || String(item.is_percent).toLowerCase() === 'true',
          rent_dollar: item.rent_dollar != null ? item.rent_dollar : '',
          rent_percent: item.rent_percent != null ? item.rent_percent : '',
        },
      }));
      modalFieldChangeHandler('customLeaseEscalation', { value: dbSchedule }, 'single');
    }
    modalFieldChangeHandler('fixedLeaseEscalation', {
      value: {
        rent_escalation_dollar: compData.rent_escalation_dollar,
        rent_escalation_percent: compData.rent_escalation_percent,
        rent_escalation_is_percent: compData.rent_escalation_is_percent,
      },
    }, 'single');
    modalFieldChangeHandler('tenantImprovementAllowance', {
      value: compData.tenant_improvement_allowance_amount_per_sqft != null
        ? String(compData.tenant_improvement_allowance_amount_per_sqft) : '',
    }, 'single');
    modalFieldChangeHandler('parkingRatio', { value: compData.parking_ratio || '' }, 'single');
    modalFieldChangeHandler('tenantImprovementDetails', { value: compData.tenant_improvements_detail || '' }, 'single');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compData?.id, deal.rep_role]);

  // ─── PDF page navigation for chip page refs ───
  const handleFieldRefClick = useCallback((label, pageNumber) => {
    if (label !== 'N/A' && pageNumber !== -1) {
      setPdfPageData({
        pageNumber,
        annotationField: `${PARENT_KEYS[label]}_${TRACKING_IDS[label]}`,
      });
    } else {
      setPdfPageData({ pageNumber, annotationField: label });
    }
  }, []);

  const abstractionContextValue = {
    // Marks this as the new enhanced split-pane flow (see leaseAbstractionContext.js).
    isEnhancedFlow: true,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    isEarlyInvoice,
    goToPage: useCallback((pageNumber) => {
      setPdfPageData({ pageNumber: parseInt(pageNumber, 10), annotationField: 'N/A' });
    }, []),
    isMagnified: false,
    setPdfPageData,
    findFieldRef: handleFieldRefClick,
    currentHighlightedField,
    setCurrentHighlightedField,
    saveCompField,
    clearFieldError,
  };

  return (
    <LeaseAbstractionContext.Provider value={abstractionContextValue}>
      <div
        className="voucher-lease-abstraction-layout"
        ref={containerRef}
        style={{ '--lease-abstraction-left-pane-width': getSplitPaneWidthCssValue(leftPaneWidth) }}
      >

        {/* LEFT: instructions + lease comp form + footer */}
        <div className="voucher-lease-abstraction-layout__left">

          {/* Instructions banner */}
          <div className="lease-abstraction-instructions">
              <div className="lease-abstraction-instructions__row">
                <p>Instructions</p>
                <button
                  type="button"
                  className="lease-abstraction-instructions__show-more"
                  onClick={() => setShowMoreInstructions(!showMoreInstructions)}
                >
                  {showMoreInstructions ? 'Show Less' : 'Show More'}
                </button>
              </div>
              <p className="lease-abstraction-instructions__line1">1. Review the imported data and overwrite fields as needed</p>
              {showMoreInstructions && (
                <div className="lease-abstraction-instructions__extra">
                  <ul>
                    <li>Click page number to navigate to the area of the document the value was found</li>
                    <li>
                      If the system detects a different value, underlined{' '}
                      <strong style={{ color: 'var(--t-semantic-info-sem-info-text, #5050BB)' }}>purple text</strong> will appear next to
                      the field. Click to replace the existing value with the value found by the
                      current abstraction.
                    </li>
                  </ul>
                  <p>2. Once you&apos;ve validated the information, check the box at the bottom of
                    the page, confirming you have validated the imported data, then select Continue.
                  </p>
                  <p>3. Repeat the process on each page.</p>
                  <p className="lease-abstraction-instructions__fuzzy-note">
                    * indicates the page number was derived using fuzzy logic and may be inaccurate
                    at times
                  </p>
                </div>
              )}
            </div>

          {/* Confidence legend: always visible */}
          <div className="acc-confidence-legend">
            <div className="acc-confidence-badge acc-confidence-badge--high">
              <span className="acc-confidence-badge__icon" aria-hidden="true" />
              <span>High Confidence</span>
            </div>
            <div className="acc-confidence-badge acc-confidence-badge--low">
              <span className="acc-confidence-badge__icon" aria-hidden="true" />
              <span>Low Confidence</span>
            </div>
            <span className="acc-confidence-legend__different">Different Abstracted Value</span>
            <span className="acc-confidence-legend__matching">Matching Abstracted Value</span>
          </div>

          {/* Form: extraction-chip sections + extra non-extraction sections */}
          <div className="lease-abstraction-form-scroll">
            {/* Share Comp Data banner */}
            <div className="comp-data-tab">
              <div className="informative-card">
                <h2>Share Comp Data With Research</h2>
                <p>
                  Deal IQ now supports the collection of comp data and sends it to your local research team.
                  While most of the data for a comp comes directly from the previous voucher pages,
                  the following fields are needed to ensure a full and complete comp. Once you have entered
                  the information below, you can view the completed comp on the review page.
                </p>
              </div>
            </div>
            <FileUploadLeaseComp dealId={deal.id} onGeneralInformationExpand={loadGeneralInformation} />
            <LeaseCompExtraFields />
          </div>

          {/* Footer: T&C checkbox + Back/Continue */}
          <div className="lease-abstraction-footer">
            {activeFile && (
              <div className="lease-abstraction-footer__checkbox">
                <Checkbox
                  checked={tAndCLeaseComp}
                  onChange={({ target: { checked } }) => handleLeaseCompValidationChange(checked)}
                >
                  I have validated the data being imported
                </Checkbox>
              </div>
            )}
            <div className="lease-abstraction-footer__actions">
              {abstractionPrev && (
                <Link to={abstractionPrev.url} className="cancel-btn">BACK</Link>
              )}
              {abstractionNext && (
                <Link
                  to={(!activeFile || tAndCLeaseComp) ? abstractionNext.url : '#'}
                  className={`upload-btn${(activeFile && !tAndCLeaseComp) ? ' disabled' : ''}`}
                  onClick={(e) => { if (activeFile && !tAndCLeaseComp) e.preventDefault(); }}
                >
                  CONTINUE
                </Link>
              )}
            </div>
          </div>

          {/* Disclaimer — only when extraction file present */}
          {activeFile && (
            <div className="lease-abstraction-disclaimer">
              <p className="lease-abstraction-disclaimer__title">Disclaimer</p>
              <p>
                The information contained herein includes data that has been extracted using
                artificial intelligence technology. While the technology has been tested for accuracy
                and reliability, the data in each field have not been individually verified for
                accuracy or reliability. CBRE, Inc. makes no guarantee, representation or warranty
                and accepts no responsibility or liability as to the accuracy, completeness, or
                reliability of the extracted data contained herein. You should conduct a careful,
                individual investigation of all information. Any reliance on this material is solely
                at your own risk.
              </p>
            </div>
          )}
        </div>

        <div
          className="voucher-lease-abstraction-layout__divider"
          role="separator"
          tabIndex={0}
          aria-label="Resize form and PDF panels"
          aria-orientation="vertical"
          aria-valuemin={minLeftWidth}
          aria-valuemax={maxLeftWidth}
          aria-valuenow={Math.round(leftPaneWidth)}
          onMouseDown={startResize}
          onTouchStart={startResize}
          onKeyDown={handleDividerKeyDown}
          title={dividerHintText}
          style={{ '--split-pane-hint-text': `'${dividerHintText}'` }}
        />
        {/* RIGHT: PDF viewer panel */}
        <div className="voucher-lease-abstraction-layout__right">
          <LeasePdfViewerPanel
            dealFiles={dealFiles || []}
            initialFile={activeFile || null}
            onFileChange={(file) => {
              suppressPersistedRefreshForFileRef.current = file?.id || null;
              setOverrideFile(file);
              try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
            }}
            onFileUploaded={(file) => {
              suppressPersistedRefreshForFileRef.current = null;
              setOverrideFile(file);
              try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
            }}
            uploadFile={uploadFile}
            dealId={deal.id}
            extractionResults={extractionResults}
            pdfPageData={pdfPageData}
            setPdfPageData={setPdfPageData}
            setCurrentHighlightedField={setCurrentHighlightedField}
            defaultActiveTab="comments"
            tabStorageContext="financials-lease-comp"
          />
        </div>
      </div>
    </LeaseAbstractionContext.Provider>
  );
};

LeaseCompAbstractionTab.propTypes = {
  deal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    deal_type: PropTypes.string,
    rep_role: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  dealFiles: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-next-line react/forbid-prop-types
  abstractionLeaseFile: PropTypes.object,
  fetchFiles: PropTypes.func,
  uploadFile: PropTypes.func,
};

LeaseCompAbstractionTab.defaultProps = {
  dealFiles: [],
  abstractionLeaseFile: null,
  fetchFiles: () => {},
  uploadFile: () => {},
};

export default LeaseCompAbstractionTab;
